import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Bell,
  Building2,
  ClipboardCheck,
  Contact,
  FileStack,
  FileText,
  Gauge,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu as MenuIcon,
  Settings,
  ShieldCheck,
  UserCircle,
  Users,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { t } from '../lib/i18n';
import { timeAgo } from '../lib/format';
import { Avatar, IconButton, Menu, MenuItem, cx } from './ui';
import type { NotificationList } from '../../shared/api';
import type { Permission } from '../../shared/permissions';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perms?: Permission[];
  any?: boolean;
  end?: boolean;
}

const MAIN_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard />, end: true },
  { to: '/templates', label: 'Templates', icon: <FileStack />, perms: ['templates.view'] },
  { to: '/documents', label: 'Documents', icon: <FileText /> },
  { to: '/reviews', label: 'Reviews', icon: <ClipboardCheck />, perms: ['documents.review'] },
  { to: '/customers', label: 'Customers', icon: <Contact />, perms: ['customers.view'] },
];

const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: <Gauge />, perms: ['admin.dashboard'], end: true },
  { to: '/admin/users', label: 'Users', icon: <Users />, perms: ['users.manage'] },
  { to: '/admin/roles', label: 'Roles', icon: <ShieldCheck />, perms: ['users.manage'] },
  { to: '/admin/audit', label: 'Audit log', icon: <ListChecks />, perms: ['audit.view'] },
  { to: '/admin/jobs', label: 'Background jobs', icon: <Activity />, perms: ['org.manage', 'system.manage'], any: true },
  { to: '/admin/organisation', label: 'Organisation', icon: <Settings />, perms: ['org.manage'] },
  { to: '/admin/organisations', label: 'All organisations', icon: <Building2 />, perms: ['system.manage'] },
];

function NotificationsBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationList>('/notifications'),
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: (ids?: string[]) => api.post('/notifications/read', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = data?.unread ?? 0;
  return (
    <Menu
      width="w-80"
      trigger={() => (
        <IconButton label={t('Notifications')} className="relative">
          <Bell className="size-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </IconButton>
      )}
    >
      {(close) => (
        <div>
          <div className="flex items-center justify-between border-b border-slate-100 px-3 pb-2 pt-1">
            <span className="text-sm font-semibold text-slate-800">{t('Notifications')}</span>
            {unread > 0 && (
              <button type="button" className="text-xs font-medium text-brand-600 hover:underline" onClick={() => markRead.mutate(undefined)}>
                {t('Mark all read')}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {(data?.items ?? []).length === 0 && <p className="px-3 py-6 text-center text-sm text-slate-500">{t('You are all caught up.')}</p>}
            {(data?.items ?? []).slice(0, 15).map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  if (!n.readAt) markRead.mutate([n.id]);
                  close();
                  if (n.link) navigate(n.link);
                }}
                className={cx('block w-full border-b border-slate-50 px-3 py-2.5 text-left hover:bg-slate-50', !n.readAt && 'bg-brand-50/50')}
              >
                <div className="flex items-start gap-2">
                  {!n.readAt && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-500" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{n.title}</p>
                    <p className="line-clamp-2 text-xs text-slate-500">{n.body}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">{timeAgo(n.createdAt)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <Link to="/notifications" onClick={close} className="block px-3 pt-2 pb-1 text-center text-xs font-medium text-brand-600 hover:underline">
            {t('View all')}
          </Link>
        </div>
      )}
    </Menu>
  );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { can, canAny } = useAuth();
  const visible = (item: NavItem) => !item.perms || (item.any ? canAny(...item.perms) : can(...item.perms));
  const admin = ADMIN_NAV.filter(visible);
  const link = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cx(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors [&>svg]:size-[18px]',
          isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        )
      }
    >
      {item.icon}
      {t(item.label)}
    </NavLink>
  );
  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
      {MAIN_NAV.filter(visible).map(link)}
      {admin.length > 0 && (
        <>
          <div className="mt-6 mb-1 px-3 text-xs font-semibold tracking-wider text-slate-400 uppercase">{t('Administration')}</div>
          {admin.map(link)}
        </>
      )}
    </nav>
  );
}

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <img src="/favicon.svg" alt="" className="size-8" />
      <span className="text-lg font-semibold tracking-tight text-slate-900">DocTemplate</span>
    </Link>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="flex h-full">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center border-b border-slate-100 px-5">
          <Logo />
        </div>
        <SidebarNav />
        <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-400">{user?.organisation.name}</div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl animate-slide-up">
            <div className="flex h-16 items-center justify-between border-b border-slate-100 px-5">
              <Logo />
              <IconButton label={t('Close menu')} onClick={() => setMobileOpen(false)}>
                <X className="size-5" />
              </IconButton>
            </div>
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 sm:px-6">
          <div className="flex items-center gap-2 lg:hidden">
            <IconButton label={t('Open menu')} onClick={() => setMobileOpen(true)}>
              <MenuIcon className="size-5" />
            </IconButton>
            <Logo />
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <Menu
              trigger={() => (
                <button type="button" className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-100">
                  <Avatar name={user?.name ?? ''} size="sm" />
                  <span className="hidden text-sm font-medium text-slate-700 sm:block">{user?.name}</span>
                </button>
              )}
            >
              {(close) => (
                <>
                  <div className="border-b border-slate-100 px-3 pt-1 pb-2">
                    <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="truncate text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <MenuItem
                    icon={<UserCircle />}
                    onClick={() => {
                      close();
                      navigate('/account');
                    }}
                  >
                    {t('My account')}
                  </MenuItem>
                  <MenuItem
                    icon={<LogOut />}
                    onClick={async () => {
                      close();
                      await logout();
                      navigate('/login');
                    }}
                  >
                    {t('Sign out')}
                  </MenuItem>
                </>
              )}
            </Menu>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export function AuthLayout({ children, title, subtitle }: { children: ReactNode; title: string; subtitle?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-50 px-4 py-12">
      <div className="mb-8">
        <Logo />
      </div>
      <div className="card w-full max-w-md p-8 animate-slide-up">
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
      <p className="mt-8 text-xs text-slate-400">© {new Date().getFullYear()} DocTemplate Platform</p>
    </div>
  );
}
